import React, { useEffect, useRef, useState } from "react";
import Tooltip from "../../../Project Page/Tooltip";

// Read-only display for a node's writing brief / generation prompt.
//
// This used to be a `<textarea readOnly rows={5}>`. A form control is the wrong
// element for prose: it pins the brief into a ~5-line scroll port regardless of
// length, can't render structure, and reads as an input the user ought to be able
// to type into. Briefs run several hundred words, so almost all of it was hidden.

const HelpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#9CA3AF" strokeWidth="1.6" />
    <path
      d="M9.6 9.4a2.4 2.4 0 1 1 3.3 2.2c-.7.3-1 .8-1 1.6"
      stroke="#9CA3AF"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.6" r="0.9" fill="#9CA3AF" />
  </svg>
);

const ChevronIcon = ({ up }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={up ? { transform: "rotate(180deg)" } : undefined}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Briefs from the agentic analysis are written as labelled beats —
// "ROLE & READER: …", "CONTENT BEATS, IN ORDER: …", "EVIDENCE TO CITE: …".
// Surfacing those labels turns a wall of text into something scannable.
// Requires at least two labels before restructuring, so an unlabelled brief
// renders as plain prose rather than being mangled by a bad guess.
export const parseBrief = (text) => {
  const s = String(text || "").trim();
  if (!s) return [];
  // Label = ALL-CAPS run ending in a colon, at the start or after a sentence /
  // line break. The leading group is captured (not a lookbehind) so this keeps
  // working on Safari versions without lookbehind support.
  const RE = /(^|[\n.]\s*)([A-Z][A-Z0-9 &/,'()-]{2,48}):\s+/g;
  const marks = [];
  let m;
  while ((m = RE.exec(s)) !== null) {
    marks.push({
      start: m.index + m[1].length,
      labelEnd: m.index + m[0].length,
      label: m[2].trim(),
    });
    RE.lastIndex = m.index + m[0].length;
  }
  if (marks.length < 2) return [];

  const out = [];
  if (marks[0].start > 0) {
    const lead = s.slice(0, marks[0].start).trim();
    if (lead) out.push({ label: "", body: lead });
  }
  marks.forEach((mk, i) => {
    const end = i + 1 < marks.length ? marks[i + 1].start : s.length;
    out.push({ label: mk.label, body: s.slice(mk.labelEnd, end).trim() });
  });
  return out;
};

const WritingBriefPanel = ({ label, text, tooltip, action = null }) => {
  const body = String(text || "").trim();
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [copied, setCopied] = useState(false);

  // Only offer "Show more" when the text is genuinely clipped — a short brief
  // shouldn't carry a control that does nothing.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setExpanded(false);
    setOverflows(el.scrollHeight > el.clientHeight + 4);
  }, [body]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);

  if (!body) return null;

  const parts = parseBrief(body);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
    } catch {
      /* clipboard blocked — the text is selectable, so this is non-fatal */
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[#171717] font-outfit font-medium text-[15px]">{label}</span>
          {tooltip && (
            <Tooltip content={tooltip} placement="right">
              <HelpIcon />
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {action}
          <button
            type="button"
            onClick={copy}
            className="px-2 py-1 rounded-md font-outfit text-[12px] font-medium text-[#6B7280] hover:text-[#3F3F46] hover:bg-[#F4F4F5] transition-colors"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-[#E9E3F5] bg-[#FCFAFF] overflow-hidden">
        <div className="relative">
          <div
            ref={bodyRef}
            className={`px-4 py-3 font-outfit text-[13.5px] leading-[1.7] text-[#3F3F46] ${
              expanded
                ? "max-h-[38vh] overflow-y-auto custom-scrollbar"
                : "max-h-[124px] overflow-hidden"
            }`}
          >
            {parts.length ? (
              <div className="flex flex-col gap-3">
                {parts.map((p, i) => (
                  <div key={i}>
                    {p.label && (
                      <div className="text-[10.5px] font-semibold tracking-[0.09em] text-[#8B55DD] uppercase mb-1">
                        {p.label}
                      </div>
                    )}
                    <p className="whitespace-pre-line">{p.body}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="whitespace-pre-line">{body}</p>
            )}
          </div>
          {/* Fade only while clipped, so it never floats over the last line of a
              fully-expanded brief. */}
          {!expanded && overflows && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[#FCFAFF] to-transparent" />
          )}
        </div>

        {overflows && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-2 border-t border-[#EFEAF8] font-outfit text-[12.5px] font-medium text-[#8B55DD] hover:bg-[#8B55DD0A] transition-colors"
          >
            <ChevronIcon up={expanded} />
            {expanded ? "Show less" : "Show Full Brief"}
          </button>
        )}
      </div>
    </div>
  );
};

export default WritingBriefPanel;
